import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import Button from "@/components/ui/Button";
import Modal from "@/components/ui/Modal";
import Select from "@/components/ui/Select";
import Textarea from "@/components/ui/Textarea";

const FORCE_CANCEL_REASONS = [
  "Customer Complaint",
  "Retailer Request",
  "Fraudulent Activity",
  "Duplicate Mandate",
  "Data Error",
  "Other",
].map((r) => ({ value: r, label: r }));

const REMARKS_LIMIT = 200;

interface ForceCancelModalProps {
  open: boolean;
  onClose: () => void;
  refNo: string;
  onConfirm: (reason: string, remarks: string) => void;
  loading?: boolean;
}

export default function ForceCancelModal({
  open,
  onClose,
  refNo,
  onConfirm,
  loading,
}: ForceCancelModalProps) {
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [error, setError] = useState("");

  function handleClose() {
    if (loading) return;
    setReason("");
    setRemarks("");
    setError("");
    onClose();
  }

  function handleConfirm() {
    if (!reason) {
      setError("Please select a reason");
      return;
    }
    if (!remarks.trim()) {
      setError("Remarks are required to record this administrative action");
      return;
    }
    onConfirm(reason, remarks.trim());
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Force cancel this mandate?"
      footer={
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-4">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Keep Mandate
          </Button>
          <Button variant="danger" onClick={handleConfirm} loading={loading}>
            Force Cancel
          </Button>
        </div>
      }
    >
      <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-danger-bg p-3.5">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-danger" />
        <p className="text-[13px] leading-relaxed text-red-800">
          This is an administrative override. The retailer will be notified and the change will be
          recorded against your admin account.
        </p>
      </div>
      <p className="mt-4 text-sm text-ink-secondary">
        This will stop all future debits under reference{" "}
        <span className="font-medium text-ink">{refNo}</span>. This action cannot be undone.
      </p>
      <div className="mt-4">
        <Select
          label="Reason"
          required
          options={FORCE_CANCEL_REASONS}
          placeholder="Select reason"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value) setError("");
          }}
          error={error}
        />
      </div>
      <div className="mt-4">
        <Textarea
          label="Remarks"
          required
          placeholder="Explain the reason for this administrative override"
          maxLength={REMARKS_LIMIT}
          rows={3}
          value={remarks}
          onChange={(e) => {
            setRemarks(e.target.value);
            if (e.target.value.trim()) setError("");
          }}
          error={error}
        />
        <p className="mt-1 text-right text-[11px] text-ink-muted">
          {remarks.length}/{REMARKS_LIMIT}
        </p>
      </div>
    </Modal>
  );
}